import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import logger from "../lib/logger";

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
    const [content, setContentMap] = useState({});
    const [loaded, setLoaded] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/content");
            setContentMap(r.data || {});
        } catch (err) {
            logger.warn("Content load failed, using defaults:", err?.message || err);
            setContentMap({});
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const setContent = useCallback(async (key, value) => {
        // Optimistic update
        setContentMap((m) => ({ ...m, [key]: value }));
        try {
            await api.put("/admin/content", { key, value });
            return true;
        } catch (e) {
            logger.error("Content save failed for key", key, e);
            // Roll back on failure
            await refresh();
            throw e;
        }
    }, [refresh]);

    const get = useCallback(
        (key, fallback = "") =>
            content[key] !== undefined && content[key] !== null ? content[key] : fallback,
        [content]
    );

    const value = useMemo(
        () => ({ content, get, setContent, loaded, refresh }),
        [content, get, setContent, loaded, refresh]
    );

    return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
    return useContext(ContentContext);
}
